import { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { toast } from "sonner";
import { User, Mail, Building2, Briefcase, Loader2, Eye, EyeOff, Phone } from "lucide-react";

// Using relative URL for API calls
const API = "/api";

export default function SettingsModal({ open, onOpenChange, user, onUserUpdate }) {
  const [activeTab, setActiveTab] = useState("profile");
  const [loading, setLoading] = useState(false);
  
  // Profile form state
  const [firstName, setFirstName] = useState(user?.first_name || "");
  const [lastName, setLastName] = useState(user?.last_name || "");
  const [companyName, setCompanyName] = useState(user?.company_name || "");
  const [jobTitle, setJobTitle] = useState(user?.job_title || "");
  const [companyPhone, setCompanyPhone] = useState(user?.company_phone || "");
  const [mobilePhone, setMobilePhone] = useState(user?.mobile_phone || "");
  
  // Password form state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const handleSaveProfile = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("auth_token");
      const response = await fetch(`${API}/auth/profile`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          first_name: firstName,
          last_name: lastName,
          company_name: companyName,
          job_title: jobTitle,
          company_phone: companyPhone,
          mobile_phone: mobilePhone,
        }),
        
      });

      if (!response.ok) {
        throw new Error("Failed to update profile");
      }

      const updatedUser = await response.json();
      onUserUpdate(updatedUser);
      localStorage.setItem("user", JSON.stringify(updatedUser));
      toast.success("Profile updated successfully");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("auth_token");
      const response = await fetch(`${API}/auth/change-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
        }),
        
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || "Failed to change password");
      }

      toast.success("Password changed successfully");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: "profile", label: "Profile" },
    { id: "password", label: "Password" },
    { id: "account", label: "Account" },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#1E1E1E] border-[#333] text-white max-w-lg" data-testid="settings-modal">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">Settings</DialogTitle>
        </DialogHeader>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-[#333] pb-2 mb-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                activeTab === tab.id
                  ? "bg-[#14A800] text-white"
                  : "text-[#888] hover:text-white hover:bg-[#2A2A2A]"
              }`}
              data-testid={`settings-tab-${tab.id}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Profile Tab */}
        {activeTab === "profile" && (
          <div className="space-y-4" data-testid="settings-profile-tab">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 rounded-full bg-[#333] flex items-center justify-center">
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} className="w-full h-full rounded-full object-cover" />
                ) : (
                  <User className="w-8 h-8 text-[#888]" />
                )}
              </div>
              <div>
                <p className="text-lg font-medium">{user?.name || "User"}</p>
                <p className="text-sm text-[#888]">{user?.email}</p>
              </div>
            </div>

            {/* Email (Read-only, pre-populated) */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#888]">
                <Mail className="w-4 h-4 inline mr-2" />
                Email
              </Label>
              <Input
                id="email"
                value={user?.email || ""}
                disabled
                className="bg-[#0D0D0D] border-[#333] text-[#888] cursor-not-allowed"
                data-testid="settings-email"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName" className="text-[#888]">First Name</Label>
                <Input
                  id="firstName"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="bg-[#0D0D0D] border-[#333] text-white"
                  data-testid="settings-first-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName" className="text-[#888]">Last Name</Label>
                <Input
                  id="lastName"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="bg-[#0D0D0D] border-[#333] text-white"
                  data-testid="settings-last-name"
                />
              </div>
            </div>

            {/* Phone Numbers */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="mobilePhone" className="text-[#888]">
                  <Phone className="w-4 h-4 inline mr-2" />
                  Mobile Phone
                </Label>
                <Input
                  id="mobilePhone"
                  type="tel"
                  value={mobilePhone}
                  onChange={(e) => setMobilePhone(e.target.value)}
                  placeholder="+1 (555) 123-4567"
                  className="bg-[#0D0D0D] border-[#333] text-white"
                  data-testid="settings-mobile-phone"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="companyPhone" className="text-[#888]">
                  <Building2 className="w-4 h-4 inline mr-2" />
                  Company Phone
                </Label>
                <Input
                  id="companyPhone"
                  type="tel"
                  value={companyPhone}
                  onChange={(e) => setCompanyPhone(e.target.value)}
                  placeholder="+1 (555) 987-6543"
                  className="bg-[#0D0D0D] border-[#333] text-white"
                  data-testid="settings-company-phone"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="companyName" className="text-[#888]">
                <Building2 className="w-4 h-4 inline mr-2" />
                Company Name
              </Label>
              <Input
                id="companyName"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                className="bg-[#0D0D0D] border-[#333] text-white"
                data-testid="settings-company-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="jobTitle" className="text-[#888]">
                <Briefcase className="w-4 h-4 inline mr-2" />
                Job Title
              </Label>
              <Input
                id="jobTitle"
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
                className="bg-[#0D0D0D] border-[#333] text-white"
                data-testid="settings-job-title"
              />
            </div>

            <Button
              onClick={handleSaveProfile}
              disabled={loading}
              className="w-full bg-[#14A800] hover:bg-[#108A00] text-white mt-4"
              data-testid="settings-save-profile"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Changes"}
            </Button>
          </div>
        )}

        {/* Password Tab */}
        {activeTab === "password" && (
          <div className="space-y-4" data-testid="settings-password-tab">
            <div className="space-y-2">
              <Label htmlFor="currentPassword" className="text-[#888]">Current Password</Label>
              <div className="relative">
                <Input
                  id="currentPassword"
                  type={showCurrentPassword ? "text" : "password"}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="bg-[#0D0D0D] border-[#333] text-white pr-10"
                  data-testid="settings-current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#888] hover:text-white"
                >
                  {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="newPassword" className="text-[#888]">New Password</Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="bg-[#0D0D0D] border-[#333] text-white pr-10"
                  data-testid="settings-new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#888] hover:text-white"
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-[#888]">Confirm New Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="bg-[#0D0D0D] border-[#333] text-white"
                data-testid="settings-confirm-password"
              />
            </div>

            <Button
              onClick={handleChangePassword}
              disabled={loading || !currentPassword || !newPassword || !confirmPassword}
              className="w-full bg-[#14A800] hover:bg-[#108A00] text-white mt-4"
              data-testid="settings-change-password"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Change Password"}
            </Button>
          </div>
        )}

        {/* Account Tab */}
        {activeTab === "account" && (
          <div className="space-y-4" data-testid="settings-account-tab">
            <div className="p-4 bg-[#0D0D0D] rounded-lg border border-[#333]">
              <h3 className="font-medium mb-2">Account Information</h3>
              <div className="space-y-2 text-sm text-[#888]">
                <p className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  {user?.email}
                </p>
                <p>Plan: <span className="text-[#14A800]">{user?.plan || "Free Plan"}</span></p>
                <p>Member since: {user?.created_at ? new Date(user.created_at).toLocaleDateString() : "N/A"}</p>
              </div>
            </div>

            <div className="p-4 bg-[#0D0D0D] rounded-lg border border-red-900/30">
              <h3 className="font-medium text-red-400 mb-2">Danger Zone</h3>
              <p className="text-sm text-[#888] mb-4">
                Once you delete your account, there is no going back. Please be certain.
              </p>
              <Button
                variant="outline"
                className="w-full border-red-600 text-red-400 hover:bg-red-600/10 hover:text-red-400"
                data-testid="settings-delete-account"
              >
                Delete Account
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
